/* eslint-disable react-hooks/exhaustive-deps */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import viewApi from '@shared/features/views/services/view-api';
import type { TView } from '@shared/types/view';
import { keepPreviousData } from '@tanstack/react-query';

type UseDefaultFiltersProps = {
  viewEntityType?: TView['entity_type'];
  viewEntityId?: number;
};

type TDefaultViewStateProps = {
  isDefaultViewDisabled: boolean;
  hasDefaultFilters: boolean;
  toggleDefaultFilters: () => void;
  resetToDefaultFilters: () => void;
};

const useFetchDefaultView = ({
  entity,
  entityId,
}: {
  entity?: TView['entity_type'];
  entityId?: number;
}) => {
  const data = viewApi.useFetch(
    {
      entity_type: entity,
      entity_id: entityId,
      is_default: true,
    },
    { enabled: !!entity }
  );
  return { ...data, data: data.data?.results[0] };
};

function useGetCurrentView({
  viewEntityId,
  viewEntityType,
}: UseDefaultFiltersProps) {
  const [sp] = useSearchParams();
  const viewIdParam = sp.get('view');

  const {
    data: defaultView,
    isPending: isDefaultViewPending,
    isFetched: isDefaultViewFetched,
  } = useFetchDefaultView({
    entity: viewEntityType,
    entityId: viewEntityId,
  });

  const {
    data: view,
    isPending: isViewPending,
    isFetched: isViewFetched,
  } = viewApi.useFetchOne(
    {
      id: viewIdParam,
    },
    { placeholderData: keepPreviousData }
  );

  const isAllPending = isDefaultViewPending || isViewPending;
  const isAllFetched = isDefaultViewFetched || isViewFetched;

  const [currentView, setCurrentView] = useState<TView | undefined>(undefined);

  useEffect(
    function syncStateWithDefaultViewInitially() {
      if (!viewIdParam) {
        setCurrentView(defaultView);
      }
    },
    [isDefaultViewPending]
  );

  const apiView = viewIdParam ? view : undefined;
  useEffect(
    function syncStateWithViewWhenParamChanged() {
      if (apiView) {
        setCurrentView(apiView);
      }
    },
    [isViewPending, JSON.stringify(apiView)]
  );

  return {
    currentView,
    setCurrentView,
    defaultView,
    isViewPending: isAllPending,
    isViewFetched: isAllFetched,
  };
}

export { useGetCurrentView, type TDefaultViewStateProps };
